import dayjs from "dayjs";
import { useQuery } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loader from "../../../common/components/loader/Loader";
import QueryErrorViewer from "../../../error/components/error-viewer/QueryErrorViewer";
import { createOidcUserManagerFromSettings } from "../utils/utils";

/**
 * OAuth callback for copy-auth RingCentral flow.
 * Unlike RingcentralAuthCallback, this writes tokens into
 * taskConfig.copyConfig.sourceConfigData instead of taskConfig directly.
 *
 * Form values are stored as plain JSON in state (no parser-specific serialization),
 * so they can be restored regardless of the main task type.
 */
function CopyRingcentralAuthCallback() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const { isError, error } = useQuery({
        queryKey: ['auth/copy-ringcentral/callback', navigate, searchParams],
        retry: 0,
        queryFn: async () => {
            const state = searchParams.get('state');
            if (!state) throw new Error('State not found');
            const key = `oidc.${state}`;
            const settings = sessionStorage.getItem(key);
            if (!settings) throw new Error('Ringcentral auth settings not found');
            const parsedSettings = JSON.parse(settings);
            const userManager = createOidcUserManagerFromSettings({
                authorityUri: parsedSettings.authority,
                clientId: parsedSettings.client_id,
                clientSecret: parsedSettings.client_secret,
                redirectUri: parsedSettings.redirect_uri,
            })
            const user = await userManager.signinCallback();
            if (!user) throw new Error('User not found');

            const formValues = (user.state as any).formValues;
            const from = (user.state as any).location;

            await userManager.clearStaleState();
            await userManager.removeUser();

            if (formValues && from) {
                // formValues is the full Formik values object stored as plain JSON.
                // We inject the OAuth tokens into copyConfig.sourceConfigData.
                const updatedValues = {
                    ...formValues,
                    taskConfig: {
                        ...formValues.taskConfig,
                        copyConfig: {
                            ...formValues.taskConfig?.copyConfig,
                            sourceConfigData: {
                                ...formValues.taskConfig?.copyConfig?.sourceConfigData,
                                accessToken: user.access_token,
                                expiresAt: dayjs().add(user.expires_in ?? 60, 'second').toISOString(),
                                refreshToken: user.refresh_token!,
                                refreshTokenExpiresAt: (user as any).refresh_token_expires_in
                                    ? dayjs().add((user as any).refresh_token_expires_in, 'second').toISOString()
                                    : dayjs().add(7, 'day').toISOString()
                            }
                        }
                    }
                };

                navigate(from, { state: updatedValues });
            }
        }
    })

    if (isError) return <QueryErrorViewer error={error} />
    return <Loader />
}

export default CopyRingcentralAuthCallback
